import ScratchJr from '../../../ScratchJr';
import Palette from '../../../ui/Palette';
import Thumbs from '../../../ui/Thumbs';
import ActivityStorageService from '../../storage/ActivityStorageService';
import ActivityAudio from '../../shared/ActivityAudio';
import {scorePreferredNarrationVoice} from '../../shared/SpeechVoicePreference';
import PaletteRestrictor from '../guided-creation/PaletteRestrictor';
import {gn} from '../../../../utils/lib';

const DEFAULT_PHASE = 'INTRO';
const PHASES = Object.freeze({
    INTRO: 'INTRO',
    TASK_1_DRUMS: 'TASK_1_DRUMS',
    CHAR_SWITCH_1: 'CHAR_SWITCH_1',
    TASK_2_GUITAR: 'TASK_2_GUITAR',
    DEBUG_INTERLUDE: 'DEBUG_INTERLUDE',
    CHAR_SWITCH_2: 'CHAR_SWITCH_2',
    TASK_3_SINGER: 'TASK_3_SINGER',
    FULL_CONCERT: 'FULL_CONCERT',
    BONUS: 'BONUS',
    COMPLETE: 'COMPLETE'
});

const MEMBERS = ['bluux', 'dog', 'bird'];

export default class BugBandFlow {
    constructor (activityData, stateMachine, container) {
        this._activityData = activityData;
        this._stateMachine = stateMachine;
        this._container = container;
        this._stage = container.querySelector('.stage') || container;
        this._paletteRestrictor = null;
        this._overlay = null;
        this._lights = null;
        this._audience = null;
        this._toast = null;
        this._doneBtn = null;
        this._completionOverlay = null;
        this._ghostHand = null;
        this._phase = DEFAULT_PHASE;
        this._config = this._buildConfig(activityData);
        this._idleTimers = [];
        this._phaseTimeout = null;
        this._lastInteractionAt = Date.now();
        this._sessionStartedAt = Date.now();
        this._idleTimeMs = 0;
        this._idlePausedAt = null;
        this._selectedRole = null;
        this._hintEvents = [];
        this._voiceHistory = [];
        this._lastNarrationId = null;
        this._lastNarrationText = '';
        this._speechVoice = null;
        this._speechVoiceScore = 0;
        this._speechQueue = Promise.resolve();
        this._speechRunId = 0;
        this._interactionSettleTimer = null;
        this._autoDebugPlaybackPending = false;
        this._voicesChangeHandler = this._refreshPreferredVoice.bind(this);
        this._destroyed = false;
        this._scriptSignatures = {};
        this._runMonitor = null;
        this._runMonitorWarmup = null;
        this._runObservation = null;
        this._flagTapCount = 0;
        this._characterSwitches = 0;
        this._debugAttempts = 0;
        this._debugSuccess = false;
        this._bugInjected = false;
        this._injectedBugSignature = null;
        this._repeatAnnounced = false;
        this._outcomeFlags = {
            outcome_1: false,
            outcome_2: false,
            outcome_3: false,
            outcome_4: false,
            outcome_5: false,
            outcome_6: false
        };
        this._milestones = {
            bluuxFlag: false,
            bluuxReady: false,
            dogFlag: false,
            dogReady: false,
            birdFlag: false,
            birdReady: false
        };
        this._onPointerDown = this._handleMeaningfulInteraction.bind(this);
        this._onVisibilityChange = this._stopNarration.bind(this);
        this._onPageHide = this._stopNarration.bind(this);
    }

    async init () {
        this._renderChrome();
        this._paletteRestrictor = new PaletteRestrictor(this._container, []);
        this._attachListeners();
        this._initVoiceSelection();
        await this._waitForPreferredVoice();
        this._refreshScriptSignatures();

        const saved = await ActivityStorageService.getActivityProgress(this._activityData.activityId);
        if (saved && saved.outcomeFlags) {
            this._outcomeFlags = {...this._outcomeFlags, ...saved.outcomeFlags};
        }
        if (saved && Array.isArray(saved.hintEvents)) {
            this._hintEvents = saved.hintEvents.slice();
        }
        if (saved && typeof saved.flagTapCount === 'number') {
            this._flagTapCount = saved.flagTapCount;
        }
        if (saved && typeof saved.characterSwitches === 'number') {
            this._characterSwitches = saved.characterSwitches;
        }
        if (saved && typeof saved.debugAttempts === 'number') {
            this._debugAttempts = saved.debugAttempts;
        }
        if (saved && typeof saved.debugSuccess === 'boolean') {
            this._debugSuccess = saved.debugSuccess;
        }
        if (saved && typeof saved.bugInjected === 'boolean') {
            this._bugInjected = saved.bugInjected;
        }
        if (saved && typeof saved.injectedBugSignature === 'string') {
            this._injectedBugSignature = saved.injectedBugSignature;
        }
        if (saved && typeof saved.repeatAnnounced === 'boolean') {
            this._repeatAnnounced = saved.repeatAnnounced;
        }
        if (saved && saved.milestones) {
            const migratedMilestones = {...saved.milestones};
            if (typeof migratedMilestones.bluuxFlag === 'undefined' && typeof migratedMilestones.catFlag !== 'undefined') {
                migratedMilestones.bluuxFlag = migratedMilestones.catFlag;
            }
            if (typeof migratedMilestones.bluuxReady === 'undefined' && typeof migratedMilestones.catReady !== 'undefined') {
                migratedMilestones.bluuxReady = migratedMilestones.catReady;
            }
            this._milestones = {...this._milestones, ...migratedMilestones};
        }
        const restoredScripts = this._restoreSavedScripts(saved);

        const savedPhase = saved && saved.currentState && PHASES[saved.currentState]
            ? saved.currentState
            : null;
        if (savedPhase) {
            const resumePhase = savedPhase === PHASES.FULL_CONCERT ? PHASES.BONUS : savedPhase;
            this._setPhase(resumePhase, {silent: true});
            this._ensureExpectedSelection();
            this._syncVisualState();
            this._startIdleScaffolding();
            if (savedPhase === PHASES.DEBUG_INTERLUDE && this._bugInjected) {
                if (!restoredScripts) {
                    this._injectBluuxBug();
                } else if (!this._bluuxHasInjectedBug()) {
                    this._injectBluuxBug();
                }
            }
            if (resumePhase === PHASES.BONUS) {
                this._showDoneButton();
            }
            this._queueNarration('VO-01', this._buildResumePrompt(resumePhase), {
                speaker: this._speakerForPhase(resumePhase),
                clear: true
            }).then(() => {
                if (!this._destroyed && this._phase === PHASES.INTRO) {
                    this._advanceFromIntro();
                }
            });
            await this._persistProgress();
            return;
        }

        this._setPhase(PHASES.INTRO, {silent: true});
        this._selectRole('bluux');
        this._syncVisualState();
        this._queueNarration('VO-01', this._config.voiceOver['VO-01'], {speaker: 'bluux', clear: true})
            .then(() => {
                if (!this._destroyed && this._phase === PHASES.INTRO) {
                    this._advanceFromIntro();
                }
            });
        await this._persistProgress();
    }

    onStateChange (prevState, newState) {
        if (prevState === 'SUCCESS' && newState === 'BUILDING') {
            this._resetForReplay();
            return;
        }

        if (newState === 'RUNNING') {
            this._flagTapCount++;
            this._clearIdleTimers();
            this._beginRunObservation();
            return;
        }

        if (newState === 'VALIDATING') {
            this._stopRunMonitor();
            this._updateRunObservation();
            this._validateRun();
            return;
        }

        if (newState === 'BUILDING' || newState === 'READY') {
            this._stopRunMonitor();
            this._syncVisualState();
            this._startIdleScaffolding();
        }
    }

    getBlockCount () {
        return ScratchJr.collectActivityBlocks().length;
    }

    evaluate () {
        return {
            success: this._phase === PHASES.COMPLETE,
            usedBlocks: this.getBlockCount(),
            storyPhase: this._phase,
            flagTapCount: this._flagTapCount,
            debugAttempts: this._debugAttempts
        };
    }

    showHint () {
        this._deliverIdleScaffold(1);
    }

    captureRunStartPosition () {
        // ScratchJr handles sprite reset for each green-flag run.
    }

    restartActivity () {
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }
        this._resetForReplay();
        return true;
    }

    destroy () {
        this._destroyed = true;
        this._clearIdleTimers();
        this._stopRunMonitor();
        if (this._runMonitorWarmup) {
            clearTimeout(this._runMonitorWarmup);
            this._runMonitorWarmup = null;
        }
        if (this._interactionSettleTimer) {
            clearTimeout(this._interactionSettleTimer);
            this._interactionSettleTimer = null;
        }
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._clearHintVisuals();
        this._hideDoneButton();
        this._dismissCompletionModal();
        this._closeAllBubbles();
        this._stopNarration();
        if (window.speechSynthesis && typeof window.speechSynthesis.removeEventListener === 'function') {
            window.speechSynthesis.removeEventListener('voiceschanged', this._voicesChangeHandler);
        }
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        window.removeEventListener('pagehide', this._onPageHide);
        window.removeEventListener('beforeunload', this._onPageHide);
        document.removeEventListener('pointerdown', this._onPointerDown, true);
        if (this._paletteRestrictor) {
            this._paletteRestrictor.destroy();
            this._paletteRestrictor = null;
        }
        if (this._overlay && this._overlay.parentNode) {
            this._overlay.parentNode.removeChild(this._overlay);
            this._overlay = null;
        }
    }

    _buildConfig (activityData) {
        const bugBand = activityData.bugBand || {};
        const firstPage = activityData.projectTemplate && activityData.projectTemplate.pages
            ? activityData.projectTemplate.pages[0]
            : {};
        const sprites = Array.isArray(firstPage.sprites) ? firstPage.sprites : [];
        const byName = sprites.reduce((acc, sprite) => {
            acc[String(sprite && sprite.name || '').toLowerCase()] = sprite;
            return acc;
        }, {});

        const bluux = byName.bluux || byName.penguin || byName.cat || {name: 'Bluux', x: 120, y: 210};
        const dog = byName.dog || {name: 'Dog', x: 240, y: 212};
        const bird = byName.bird || {name: 'Bird', x: 352, y: 170};

        return {
            introDelayMs: bugBand.introDelayMs || 4800,
            restartPhase: bugBand.restartPhase || PHASES.TASK_1_DRUMS,
            availableBlocks: bugBand.availableBlocks || {},
            idleScaffolding: bugBand.idleScaffolding || {},
            voiceOver: bugBand.voiceOver || {},
            members: {
                bluux: {
                    role: 'bluux',
                    spriteName: bluux.name || 'Bluux',
                    speaker: 'bluux',
                    homeX: bluux.x || 120,
                    homeY: bluux.y || 210,
                    promptCategory: 3,
                    expectedAction: 'playsnd'
                },
                dog: {
                    role: 'dog',
                    spriteName: dog.name || 'Dog',
                    speaker: 'dog',
                    homeX: dog.x || 240,
                    homeY: dog.y || 212,
                    promptCategory: 1,
                    expectedAction: 'hop'
                },
                bird: {
                    role: 'bird',
                    spriteName: bird.name || 'Bird',
                    speaker: 'bird',
                    homeX: bird.x || 352,
                    homeY: bird.y || 170,
                    promptCategory: 2,
                    expectedAction: 'grow'
                }
            },
            bugBlock: {type: 'forward', arg: 1},
            voiceConfidenceThreshold: 90
        };
    }

    _renderChrome () {
        if (this._overlay) {
            return;
        }

        this._overlay = document.createElement('div');
        this._overlay.className = 'activity-bug-band-layer';
        this._lights = document.createElement('div');
        this._lights.className = 'activity-bug-band-lights';
        this._overlay.appendChild(this._lights);

        this._audience = document.createElement('div');
        this._audience.className = 'activity-bug-band-audience';
        for (let i = 0; i < 7; i++) {
            const member = document.createElement('span');
            member.className = 'activity-bug-band-audience-member';
            this._audience.appendChild(member);
        }
        this._overlay.appendChild(this._audience);
        this._stage.appendChild(this._overlay);
    }

    _attachListeners () {
        document.addEventListener('pointerdown', this._onPointerDown, true);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
        window.addEventListener('pagehide', this._onPageHide);
        window.addEventListener('beforeunload', this._onPageHide);
    }

    _handleMeaningfulInteraction () {
        if (this._idlePausedAt) {
            this._idleTimeMs += Math.max(0, Date.now() - this._idlePausedAt);
            this._idlePausedAt = null;
        }

        this._lastInteractionAt = Date.now();
        this._clearIdleTimers();
        this._clearHintVisuals();
        this._startIdleScaffolding();
        if (this._interactionSettleTimer) {
            clearTimeout(this._interactionSettleTimer);
        }
        this._interactionSettleTimer = setTimeout(() => {
            this._interactionSettleTimer = null;
            this._settleInteraction();
        }, 80);
    }

    _settleInteraction () {
        const selectedRole = this._getSelectedRole();
        if (selectedRole && selectedRole !== this._selectedRole) {
            this._selectedRole = selectedRole;
            this._characterSwitches++;
            this._pulseSpriteThumb(selectedRole);
            ActivityAudio.play('sparkle');
            if (this._phase === PHASES.CHAR_SWITCH_1 && selectedRole === 'dog') {
                this._enterTask2();
                return;
            }
            if (this._phase === PHASES.CHAR_SWITCH_2 && selectedRole === 'bird') {
                this._enterTask3();
                return;
            }
        }

        const previous = {...this._scriptSignatures};
        this._refreshScriptSignatures();
        MEMBERS.forEach((role) => {
            if (previous[role] !== this._scriptSignatures[role]) {
                this._handleScriptChange(role, previous[role], this._scriptSignatures[role]);
            }
        });

        this._persistProgress();
    }

    _handleScriptChange (role, previousSignature, nextSignature) {
        if (!nextSignature) {
            return;
        }

        if (this._phase === PHASES.TASK_1_DRUMS && role === 'bluux') {
            let queuedNarration = false;
            if (!this._milestones.bluuxFlag && this._roleHasBlock(role, 'onflag')) {
                this._milestones.bluuxFlag = true;
                this._queueNarration('VO-15', null, {speaker: 'system', clear: true});
                this._selectCategory(this._config.members.bluux.promptCategory);
                queuedNarration = true;
            }
            if (!this._milestones.bluuxReady && this._isBluuxScriptValid()) {
                this._milestones.bluuxReady = true;
                this._queueNarration('VO-16', null, {speaker: 'bluux', clear: !queuedNarration});
                return;
            }
        }

        if (this._phase === PHASES.TASK_2_GUITAR && role === 'dog') {
            let queuedNarration = false;
            if (!this._milestones.dogFlag && this._roleHasBlock(role, 'onflag')) {
                this._milestones.dogFlag = true;
                this._selectCategory(this._config.members.dog.promptCategory);
                this._queueNarration('VO-17', 'Great! Dog has the green flag. Now add a move or sound block.', {
                    speaker: 'system',
                    clear: true
                });
                queuedNarration = true;
            }
            if (!this._repeatAnnounced && this._countScriptBlocks(role) >= 2) {
                this._repeatAnnounced = true;
                this._showToast('unlock', 'New!');
                this._syncVisualState();
                this._selectCategory(4);
                this._queueNarration('VO-07', null, {speaker: 'system'});
                queuedNarration = true;
            }
            if (!this._milestones.dogReady && this._isDogScriptValid()) {
                this._milestones.dogReady = true;
                this._queueNarration('VO-20', 'Tap the green flag to hear Blooks and Dog together!', {
                    speaker: 'system',
                    clear: !queuedNarration
                });
                return;
            }
        }

        if (this._phase === PHASES.DEBUG_INTERLUDE && role === 'bluux' && previousSignature) {
            if (this._autoDebugPlaybackPending && !this._bluuxHasInjectedBug()) {
                this._autoDebugPlaybackPending = false;
            }
            this._outcomeFlags.outcome_5 = true;
            if (!this._bluuxHasInjectedBug()) {
                this._queueNarration('VO-18', null, {speaker: 'bluux', clear: true});
            }
            return;
        }

        if (this._phase === PHASES.TASK_3_SINGER && role === 'bird') {
            let queuedNarration = false;
            if (!this._milestones.birdFlag && this._roleHasBlock(role, 'onflag')) {
                this._milestones.birdFlag = true;
                this._selectCategory(this._config.members.bird.promptCategory);
                this._queueNarration('VO-19', 'Wonderful! Bird is ready for the big concert!', {
                    speaker: 'bird',
                    clear: true
                });
                queuedNarration = true;
            }
            if (!this._milestones.birdReady && this._isBirdScriptValid()) {
                this._milestones.birdReady = true;
                this._queueNarration('VO-20', null, {speaker: 'system', clear: !queuedNarration});
            }
        }
    }

    _validateRun () {
        const statuses = this._getCharacterStatuses();

        if (this._phase === PHASES.TASK_1_DRUMS) {
            if (statuses.bluux.valid) {
                this._outcomeFlags.outcome_1 = true;
                this._showSpriteBubble('bluux', "I'm playing!", 2500);
                this._setPhase(PHASES.CHAR_SWITCH_1);
                this._queueNarration('VO-04', null, {speaker: 'bluux', clear: true});
                setTimeout(() => {
                    if (!this._destroyed) {
                        this._showSpriteBubble('dog', 'My turn!', 2500);
                    }
                }, 2800);
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.TASK_2_GUITAR) {
            if (statuses.bluux.valid && statuses.dog.valid) {
                this._outcomeFlags.outcome_2 = true;
                this._showSpriteBubble('dog', "Woof! I'm playing!", 2500);
                this._showSpriteBubble('bluux', "We're a band!", 2500);
                this._queueNarration('VO-06', null, {speaker: 'dog', clear: true});
                this._setPhase(PHASES.DEBUG_INTERLUDE);
                this._injectBluuxBug();
                this._queueNarration('VO-08', null, {speaker: 'bluux'}).then(() => {
                    if (!this._destroyed && this._phase === PHASES.DEBUG_INTERLUDE) {
                        this._showSpriteBubble('bluux', 'Oops!', 2000);
                        this._showSpriteBubble('dog', 'What happened?', 2000);
                        this._autoRunInjectedBug();
                    }
                });
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.DEBUG_INTERLUDE) {
            const bluuxScriptFixed = !this._bluuxHasInjectedBug();
            if (this._autoDebugPlaybackPending && !bluuxScriptFixed) {
                this._stateMachine.transition('BUILDING');
                this._persistProgress();
                return;
            }
            this._debugAttempts++;
            this._autoDebugPlaybackPending = false;
            const bluuxPlaybackLooksStable = (this._bluuxStayedOnDrums() || this._bluuxUsesNoTravelBlocks()) && this._isBluuxScriptValid();
            if (bluuxScriptFixed && bluuxPlaybackLooksStable) {
                this._debugSuccess = true;
                this._bugInjected = false;
                this._injectedBugSignature = null;
                this._outcomeFlags.outcome_3 = true;
                this._showSpriteBubble('bluux', 'You fixed it!', 2500);
                this._queueNarration('VO-10', null, {speaker: 'bluux', clear: true});
                this._setPhase(PHASES.CHAR_SWITCH_2);
                setTimeout(() => {
                    if (!this._destroyed) {
                        this._showSpriteBubble('bird', 'Me next!', 2500);
                    }
                }, 2800);
                this._queueNarration('VO-11', null, {speaker: 'bird'});
            } else {
                this._queueNarration('VO-09', null, {speaker: 'system', clear: true});
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.TASK_3_SINGER) {
            if (statuses.bluux.valid && statuses.dog.valid && statuses.bird.valid) {
                this._outcomeFlags.outcome_4 = true;
                this._outcomeFlags.outcome_6 = true;
                this._showSpriteBubble('bird', "Chirp! I'm singing!", 2500);
                this._advanceToBonus();
            }
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        if (this._phase === PHASES.BONUS || this._phase === PHASES.FULL_CONCERT) {
            this._stateMachine.transition('BUILDING');
            this._persistProgress();
            return;
        }

        this._stateMachine.transition('BUILDING');
        this._persistProgress();
    }

    _advanceFromIntro () {
        this._setPhase(PHASES.TASK_1_DRUMS);
        this._selectRole('bluux');
        this._queueNarration('VO-02', null, {speaker: 'bluux', clear: true});
        this._persistProgress();
    }

    _enterTask2 () {
        this._setPhase(PHASES.TASK_2_GUITAR);
        this._selectRole('dog');
        this._queueNarration('VO-17', 'Dog is ready! Give Dog a green flag, then add a move or sound block.', {
            speaker: 'dog',
            clear: true
        });
        this._persistProgress();
    }

    _enterTask3 () {
        this._setPhase(PHASES.TASK_3_SINGER);
        this._selectRole('bird');
        this._queueNarration('VO-11', null, {speaker: 'bird', clear: true});
        this._persistProgress();
    }

    _advanceToBonus () {
        this._setPhase(PHASES.FULL_CONCERT);
        this._launchCelebration();
        this._persistProgress({
            status: 'in_progress'
        });
        this._queueNarration('VO-12', null, {speaker: 'system', clear: true})
            .then(() => {
                if (this._destroyed) {
                    return;
                }
                this._setPhase(PHASES.BONUS);
                this._showDoneButton();
                this._queueNarration('VO-13', null, {speaker: 'system'});
                this._persistProgress();
            });
        if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
            this._stateMachine.transition('BUILDING');
        }
    }

    _completeBonus () {
        this._setPhase(PHASES.COMPLETE);
        this._hideDoneButton();
        this._closeAllBubbles();
        this._showSpriteBubble('bluux', "We're the Bug Band!", 4000);
        this._queueNarration('VO-14', null, {speaker: 'system', clear: true})
            .then(() => {
                if (this._destroyed) {
                    return;
                }
                this._closeAllBubbles();
                this._persistProgress({
                    status: 'completed',
                    stars: 3,
                    bestScore: 3,
                    completedAt: new Date().toISOString()
                });
                // Show our own completion modal -- do NOT transition to SUCCESS
                // (SUCCESS triggers the old FeedbackModal in ActivityUI)
                this._showCompletionModal();
            });
    }

    _setPhase (phase, opts = {}) {
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
            this._phaseTimeout = null;
        }
        this._phase = phase;
        this._syncVisualState();
        if (!opts.silent) {
            this._persistProgress();
        }
    }

    _syncVisualState () {
        if (this._paletteRestrictor) {
            this._paletteRestrictor.updateAllowed(this._allowedBlocksForCurrentPhase());
        }

        if (this._lights) {
            this._lights.classList.toggle('is-celebrating', this._phase === PHASES.FULL_CONCERT || this._phase === PHASES.BONUS || this._phase === PHASES.COMPLETE);
        }

        if (this._audience) {
            this._audience.classList.toggle('is-cheering', this._phase === PHASES.FULL_CONCERT || this._phase === PHASES.BONUS || this._phase === PHASES.COMPLETE);
        }

        const expectedRole = this._expectedRole();
        if (expectedRole && this._phase !== PHASES.CHAR_SWITCH_1 && this._phase !== PHASES.CHAR_SWITCH_2) {
            this._selectRole(expectedRole);
        }

        this._selectSuggestedCategory();
    }

    _allowedBlocksForCurrentPhase () {
        const allowed = (this._config.availableBlocks[this._phase] || []).slice();
        if (
            !this._repeatAnnounced &&
            (this._phase === PHASES.TASK_2_GUITAR ||
             this._phase === PHASES.DEBUG_INTERLUDE ||
             this._phase === PHASES.CHAR_SWITCH_2 ||
             this._phase === PHASES.TASK_3_SINGER ||
             this._phase === PHASES.FULL_CONCERT)
        ) {
            return allowed.filter((blockType) => blockType !== 'repeat');
        }
        return allowed;
    }

    _startIdleScaffolding () {
        this._clearIdleTimers();
        if (this._phase === PHASES.INTRO || this._phase === PHASES.FULL_CONCERT || this._phase === PHASES.COMPLETE) {
            return;
        }

        const thresholds = this._config.idleScaffolding[this._phase] || [];
        thresholds.forEach((delay, index) => {
            const tier = index + 1;
            this._idleTimers.push(setTimeout(() => {
                if (this._lastInteractionAt + delay > Date.now()) {
                    return;
                }
                if (tier === 4 && !this._idlePausedAt) {
                    this._idlePausedAt = Date.now();
                }
                this._deliverIdleScaffold(tier);
            }, delay));
        });
    }

    _clearIdleTimers () {
        while (this._idleTimers.length > 0) {
            clearTimeout(this._idleTimers.pop());
        }
    }

    _deliverIdleScaffold (tier) {
        this._hintEvents.push({
            phase: this._phase,
            tier,
            character: this._expectedRole(),
            timestamp: new Date().toISOString()
        });

        if (tier === 1) {
            this._queueNarration(`hint-${this._phase}-1`, this._hintTextForTier(1), {
                speaker: this._speakerForPhase(this._phase),
                clear: true
            });
            return;
        }

        if (tier === 2) {
            this._pulseExpectedTarget();
            return;
        }

        if (tier === 3) {
            this._highlightSuggestedBlock();
            this._queueNarration(`hint-${this._phase}-3`, this._hintTextForTier(3), {
                speaker: this._speakerForPhase(this._phase),
                clear: true
            });
            return;
        }

        if (tier === 4) {
            this._showGhostHand();
        }
    }

    _hintTextForTier (tier) {
        const expected = this._expectedRole();
        if (this._phase === PHASES.CHAR_SWITCH_1) {
            return tier >= 3 ? 'Tap on Dog to give Dog some music!' : 'Dog wants to play too! Can you tap on Dog?';
        }
        if (this._phase === PHASES.CHAR_SWITCH_2) {
            return tier >= 3 ? 'Tap on Bird so Bird can join the concert!' : 'Bird wants to play too! Can you tap on Bird?';
        }
        if (this._phase === PHASES.DEBUG_INTERLUDE) {
            return tier >= 3
                ? 'Look at Blooks blocks. Find the extra move block that does not belong.'
                : 'Something is wrong with Blooks blocks. Can you find the one that does not belong?';
        }
        if (!this._roleHasBlock(expected, 'onflag')) {
            return tier >= 3
                ? `Start with the yellow green flag block for ${this._displayName(expected)}.`
                : `${this._displayName(expected)} needs the green flag first.`;
        }

        const suggested = this._suggestedBlockType();
        if (suggested === 'playsnd') {
            return tier >= 3 ? `Try the green Pop block for ${this._displayName(expected)}.` : `${this._displayName(expected)} needs a sound block.`;
        }
        if (suggested === 'grow') {
            return tier >= 3 ? 'Try a purple Grow or Shrink block for Bird!' : 'Bird can sing with a purple looks block!';
        }
        return tier >= 3
            ? `Try a blue ${suggested === 'hop' ? 'Hop' : 'Move'} block for ${this._displayName(expected)}.`
            : `${this._displayName(expected)} needs one more block to play.`;
    }

    _pulseExpectedTarget () {
        this._clearHintVisuals();
        if (this._phase === PHASES.CHAR_SWITCH_1 || this._phase === PHASES.CHAR_SWITCH_2) {
            this._pulseSpriteThumb(this._expectedRole());
            return;
        }

        const expected = this._expectedRole();
        if (expected && this._selectedRole !== expected) {
            this._pulseSpriteThumb(expected);
            return;
        }

        const selector = this._getCategorySelector(this._suggestedCategoryIndex());
        if (selector) {
            selector.classList.add('activity-bug-band-pulse');
        }
    }

    _highlightSuggestedBlock () {
        this._clearHintVisuals();
        const block = this._findPaletteBlock(this._suggestedBlockType());
        if (block) {
            block.classList.add('activity-bug-band-target');
        } else {
            this._pulseExpectedTarget();
        }
    }

    _showGhostHand () {
        this._clearHintVisuals();

        if (this._phase === PHASES.CHAR_SWITCH_1 || this._phase === PHASES.CHAR_SWITCH_2) {
            const thumb = this._getSpriteThumb(this._expectedRole());
            if (!thumb) {
                return;
            }
            const rect = thumb.getBoundingClientRect();
            this._ghostHand = document.createElement('div');
            this._ghostHand.className = 'activity-bug-band-ghost-hand is-thumb';
            this._ghostHand.style.left = `${rect.left + rect.width / 2}px`;
            this._ghostHand.style.top = `${rect.top + rect.height / 2}px`;
            document.body.appendChild(this._ghostHand);
            setTimeout(() => this._clearHintVisuals(), 1800);
            return;
        }

        const block = this._findPaletteBlock(this._suggestedBlockType());
        const scripts = gn('scripts');
        if (!block || !scripts) {
            return;
        }

        const blockRect = block.getBoundingClientRect();
        const scriptRect = scripts.getBoundingClientRect();
        this._ghostHand = document.createElement('div');
        this._ghostHand.className = 'activity-bug-band-ghost-hand';
        this._ghostHand.style.left = `${blockRect.left + blockRect.width / 2}px`;
        this._ghostHand.style.top = `${blockRect.top + blockRect.height / 2}px`;
        this._ghostHand.style.setProperty('--activity-bug-band-ghost-dx', `${scriptRect.left + 118 - (blockRect.left + blockRect.width / 2)}px`);
        this._ghostHand.style.setProperty('--activity-bug-band-ghost-dy', `${scriptRect.top + 56 - (blockRect.top + blockRect.height / 2)}px`);
        document.body.appendChild(this._ghostHand);
        setTimeout(() => this._clearHintVisuals(), 1800);
    }

    _clearHintVisuals () {
        document.querySelectorAll('.activity-bug-band-pulse').forEach((el) => el.classList.remove('activity-bug-band-pulse'));
        document.querySelectorAll('.activity-bug-band-target').forEach((el) => el.classList.remove('activity-bug-band-target'));
        document.querySelectorAll('.activity-bug-band-thumb-pulse').forEach((el) => el.classList.remove('activity-bug-band-thumb-pulse'));
        if (this._ghostHand && this._ghostHand.parentNode) {
            this._ghostHand.parentNode.removeChild(this._ghostHand);
            this._ghostHand = null;
        }
    }

    _showDoneButton () {
        if (this._doneBtn) {
            return;
        }

        this._doneBtn = document.createElement('button');
        this._doneBtn.className = 'activity-bug-band-done-btn';
        this._doneBtn.type = 'button';
        this._doneBtn.setAttribute('aria-label', 'Done');
        this._doneBtn.textContent = 'Done';
        this._doneBtn.addEventListener('pointerup', () => {
            this._completeBonus();
        });
        this._container.appendChild(this._doneBtn);
    }

    _hideDoneButton () {
        if (this._doneBtn && this._doneBtn.parentNode) {
            this._doneBtn.parentNode.removeChild(this._doneBtn);
        }
        this._doneBtn = null;
    }

    _launchCelebration () {
        this._showToast('celebrate', 'Jam!');
        ActivityAudio.play('confetti');
        this._showSpriteBubble('bluux', 'Yeah!', 3000);
        this._showSpriteBubble('dog', 'Woof!', 3000);
        this._showSpriteBubble('bird', 'Chirp!', 3000);
    }

    _showToast(kind, text) {
        if (!this._overlay) {
            return;
        }
        if (this._toast && this._toast.parentNode) {
            this._toast.parentNode.removeChild(this._toast);
        }
        this._toast = document.createElement('div');
        this._toast.className = `activity-bug-band-toast activity-bug-band-toast-${kind}`;
        this._toast.textContent = text;
        this._overlay.appendChild(this._toast);
        setTimeout(() => {
            if (this._toast && this._toast.parentNode) {
                this._toast.parentNode.removeChild(this._toast);
                this._toast = null;
            }
        }, 1500);
    }

    _beginRunObservation () {
        const target = this._config.members.bluux;
        this._runObservation = {
            ignoreUntil: Date.now() + 180,
            bluuxMaxDistanceFromDrums: 0,
            bluuxLastPosition: target ? {
                x: target.homeX,
                y: target.homeY
            } : null
        };
        this._startRunMonitor();
    }

    _startRunMonitor () {
        this._stopRunMonitor();
        this._runMonitorWarmup = setTimeout(() => {
            this._runMonitorWarmup = null;
            this._runMonitor = setInterval(() => {
                this._updateRunObservation();
            }, 80);
        }, 180);
    }

    _stopRunMonitor () {
        if (this._runMonitorWarmup) {
            clearTimeout(this._runMonitorWarmup);
            this._runMonitorWarmup = null;
        }
        if (this._runMonitor) {
            clearInterval(this._runMonitor);
            this._runMonitor = null;
        }
    }

    _updateRunObservation () {
        if (!this._runObservation) {
            return;
        }
        if (this._runObservation.ignoreUntil && Date.now() < this._runObservation.ignoreUntil) {
            return;
        }
        const bluux = this._getSpriteOwner('bluux');
        const target = this._config.members.bluux;
        if (!bluux || !target) {
            return;
        }
        const dx = bluux.xcoor - target.homeX;
        const dy = bluux.ycoor - target.homeY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        this._runObservation.bluuxLastPosition = {
            x: bluux.xcoor,
            y: bluux.ycoor
        };
        this._runObservation.bluuxMaxDistanceFromDrums = Math.max(
            this._runObservation.bluuxMaxDistanceFromDrums || 0,
            distance
        );
    }

    _getCharacterStatuses () {
        return {
            bluux: {
                valid: this._isBluuxScriptValid(),
                x: this._getSpriteOwner('bluux') ? this._getSpriteOwner('bluux').xcoor : 0,
                y: this._getSpriteOwner('bluux') ? this._getSpriteOwner('bluux').ycoor : 0
            },
            dog: {
                valid: this._isDogScriptValid()
            },
            bird: {
                valid: this._isBirdScriptValid()
            }
        };
    }

    _isBluuxScriptValid () {
        return this._roleHasBlock('bluux', 'onflag') && this._roleHasAnyBlock('bluux', ['playsnd', 'hop']);
    }

    _isDogScriptValid () {
        return this._roleHasBlock('dog', 'onflag') && this._roleHasAnyBlock('dog', ['forward', 'back', 'up', 'down', 'hop', 'playsnd']);
    }

    _isBirdScriptValid () {
        return this._roleHasBlock('bird', 'onflag') && this._roleHasAnyBlock('bird', ['grow', 'shrink', 'playsnd', 'hop']);
    }

    _bluuxUsesNoTravelBlocks () {
        return !this._roleHasAnyBlock('bluux', ['forward', 'back', 'up', 'down']);
    }

    _roleHasBlock (role, blockType) {
        return this._getSpriteBlocks(role).some((block) => block && block.blocktype === blockType);
    }

    _roleHasAnyBlock (role, blockTypes) {
        return this._getSpriteBlocks(role).some((block) => block && blockTypes.includes(block.blocktype));
    }

    _countScriptBlocks (role) {
        return this._getSpriteBlocks(role).length;
    }

    _getSpriteBlocks (role) {
        const sprite = this._getSpriteOwner(role);
        const scriptsDiv = sprite ? gn(sprite.id + '_scripts') : null;
        if (!scriptsDiv || !scriptsDiv.owner || typeof scriptsDiv.owner.getBlocks !== 'function') {
            return [];
        }
        return scriptsDiv.owner.getBlocks() || [];
    }

    _getSpriteOwner (role) {
        const targetName = this._config.members[role] && this._config.members[role].spriteName;
        if (!targetName || !ScratchJr.stage || !ScratchJr.stage.currentPage || typeof ScratchJr.stage.currentPage.getSprites !== 'function') {
            return null;
        }
        const spriteIds = ScratchJr.stage.currentPage.getSprites();
        for (let i = 0; i < spriteIds.length; i++) {
            const spriteEl = gn(spriteIds[i]);
            const sprite = spriteEl && spriteEl.owner ? spriteEl.owner : null;
            if (sprite && String(sprite.name).toLowerCase() === String(targetName).toLowerCase()) {
                return sprite;
            }
        }
        return null;
    }

    _getSelectedRole () {
        const page = ScratchJr && ScratchJr.stage ? ScratchJr.stage.currentPage : null;
        const sprite = ScratchJr.getSprite();
        const spriteName = sprite && sprite.name
            ? String(sprite.name)
            : page && page.currentSpriteName && gn(page.currentSpriteName) && gn(page.currentSpriteName).owner
                ? String(gn(page.currentSpriteName).owner.name || '')
                : '';
        if (spriteName) {
            const normalizedName = spriteName.toLowerCase().replace(/\s+\d+$/, '');
            const matchedRole = MEMBERS.find((role) => {
                const targetName = String(this._config.members[role].spriteName || '').toLowerCase();
                return normalizedName === targetName || normalizedName.indexOf(targetName) === 0;
            });
            if (matchedRole) {
                return matchedRole;
            }
        }
        return this._selectedRole || null;
    }

    _selectRole (role) {
        const sprite = this._getSpriteOwner(role);
        if (!sprite || !ScratchJr.stage || !ScratchJr.stage.currentPage) {
            return;
        }
        // `setCurrentSprite` alone does not keep the page's sprite bookkeeping in sync.
        ScratchJr.stage.currentPage.setCurrentSprite(sprite);
        ScratchJr.stage.currentPage.currentSpriteName = sprite.id;
        ScratchJr.stage.currentPage.lastSprite = sprite.id;
        Thumbs.selectThisSprite(sprite);
        if (this._phase !== PHASES.CHAR_SWITCH_1 && this._phase !== PHASES.CHAR_SWITCH_2) {
            this._selectedRole = role;
        }
    }

    _ensureExpectedSelection () {
        const expected = this._expectedRole();
        if (expected && (this._phase !== PHASES.CHAR_SWITCH_1 && this._phase !== PHASES.CHAR_SWITCH_2)) {
            this._selectRole(expected);
        }
    }

    _expectedRole () {
        switch (this._phase) {
        case PHASES.TASK_1_DRUMS:
            return 'bluux';
        case PHASES.CHAR_SWITCH_1:
        case PHASES.TASK_2_GUITAR:
            return 'dog';
        case PHASES.DEBUG_INTERLUDE:
            return 'bluux';
        case PHASES.CHAR_SWITCH_2:
        case PHASES.TASK_3_SINGER:
        case PHASES.FULL_CONCERT:
        case PHASES.BONUS:
            return 'bird';
        default:
            return 'bluux';
        }
    }

    _speakerForPhase (phase) {
        const role = this._expectedRoleForNarration(phase);
        return role || 'system';
    }

    _expectedRoleForNarration (phase) {
        switch (phase) {
        case PHASES.TASK_1_DRUMS:
            return 'bluux';
        case PHASES.CHAR_SWITCH_1:
            return 'bluux';
        case PHASES.TASK_2_GUITAR:
            return 'dog';
        case PHASES.DEBUG_INTERLUDE:
            return 'bluux';
        case PHASES.CHAR_SWITCH_2:
        case PHASES.TASK_3_SINGER:
            return 'bird';
        default:
            return 'system';
        }
    }

    _displayName (role) {
        return this._config.members[role] ? this._config.members[role].spriteName : role;
    }

    _selectSuggestedCategory () {
        const index = this._suggestedCategoryIndex();
        if (typeof index === 'number') {
            this._selectCategory(index);
        }
    }

    _suggestedCategoryIndex () {
        if (this._phase === PHASES.CHAR_SWITCH_1 || this._phase === PHASES.CHAR_SWITCH_2) {
            return null;
        }

        const expected = this._expectedRole();
        if (!this._roleHasBlock(expected, 'onflag')) {
            return 0;
        }

        const blockType = this._suggestedBlockType();
        if (blockType === 'playsnd') {
            return 3;
        }
        if (blockType === 'grow' || blockType === 'shrink') {
            return 2;
        }
        if (blockType === 'repeat' || blockType === 'wait') {
            return 4;
        }
        return 1;
    }

    _suggestedBlockType () {
        const expected = this._expectedRole();
        if (!this._roleHasBlock(expected, 'onflag')) {
            return 'onflag';
        }

        if (this._phase === PHASES.DEBUG_INTERLUDE) {
            return 'forward';
        }

        if (expected === 'bluux' && !this._roleHasBlock('bluux', 'playsnd')) {
            return 'playsnd';
        }

        if (expected === 'dog' && !this._roleHasAnyBlock('dog', ['forward', 'back', 'up', 'down', 'hop', 'playsnd'])) {
            return 'hop';
        }

        if (expected === 'bird' && !this._roleHasAnyBlock('bird', ['grow', 'shrink'])) {
            return 'grow';
        }

        return 'playsnd';
    }

    _pulseSpriteThumb (role) {
        const thumb = this._getSpriteThumb(role);
        if (thumb) {
            thumb.classList.add('activity-bug-band-thumb-pulse');
        }
    }

    _getSpriteThumb (role) {
        const sprite = this._getSpriteOwner(role);
        return sprite && sprite.thumbnail ? sprite.thumbnail : null;
    }

    _getCategorySelector (index) {
        const selectors = document.getElementById('selectors');
        if (!selectors || typeof index !== 'number') {
            return null;
        }
        return Array.from(selectors.childNodes || []).find((child) => child && child.index === index) || null;
    }

    _findPaletteBlock (blockType) {
        const palette = document.getElementById('palette');
        if (!palette) {
            return null;
        }
        return Array.from(palette.childNodes || []).find((child) => child && child.owner && child.owner.blocktype === blockType) || null;
    }

    _selectCategory (index) {
        if (typeof index !== 'number') {
            return;
        }
        try {
            Palette.selectCategory(index);
        } catch (_) {
            // Palette not ready yet.
        }
    }

    /**
     * Show a speech bubble above a sprite by role name.
     * Mirrors StoryWalkFlow._showPlayerBubble pattern.
     */
    _showSpriteBubble (role, text, durationMs) {
        const sprite = this._getSpriteOwner(role);
        if (!sprite || typeof sprite.openBalloon !== 'function') {
            return;
        }
        sprite.openBalloon(text);
        if (durationMs && durationMs > 0) {
            setTimeout(() => {
                if (sprite && typeof sprite.closeBalloon === 'function') {
                    sprite.closeBalloon();
                }
            }, durationMs);
        }
    }

    _closeSpriteBubble (role) {
        const sprite = this._getSpriteOwner(role);
        if (sprite && typeof sprite.closeBalloon === 'function') {
            sprite.closeBalloon();
        }
    }

    _closeAllBubbles () {
        MEMBERS.forEach((role) => this._closeSpriteBubble(role));
    }

    _showCompletionModal () {
        this._completionOverlay = document.createElement('div');
        this._completionOverlay.className = 'activity-completion-overlay';

        const modal = document.createElement('div');
        modal.className = 'activity-completion-modal';

        const closeBtn = document.createElement('button');
        closeBtn.className = 'activity-completion-close';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
        });
        modal.appendChild(closeBtn);

        const header = document.createElement('div');
        header.className = 'activity-completion-header';
        const logo = document.createElement('img');
        logo.src = 'assets/lobby/scratchJrlogo.svg';
        logo.alt = 'Bluux';
        logo.className = 'activity-completion-logo';
        header.appendChild(logo);
        const title = document.createElement('h3');
        title.className = 'activity-completion-title';
        title.textContent = 'Activity Complete!';
        header.appendChild(title);
        const subtitle = document.createElement('p');
        subtitle.className = 'activity-completion-subtitle';
        subtitle.textContent = 'The Bug Band Concert';
        header.appendChild(subtitle);
        modal.appendChild(header);

        const body = document.createElement('div');
        body.className = 'activity-completion-body';

        const stars = document.createElement('div');
        stars.className = 'activity-completion-stars';
        stars.textContent = '\u2B50\u2B50\u2B50';
        body.appendChild(stars);

        const message = document.createElement('p');
        message.className = 'activity-completion-message';
        message.textContent = 'You made the Bug Band play together! Every friend has its own program, and they all play at the same time!';
        body.appendChild(message);

        const actions = document.createElement('div');
        actions.className = 'activity-completion-actions';

        const replayBtn = document.createElement('button');
        replayBtn.className = 'activity-completion-btn-replay';
        replayBtn.setAttribute('aria-label', 'Replay activity');
        replayBtn.innerHTML = '&#8634; Replay';
        replayBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            this.restartActivity();
        });
        actions.appendChild(replayBtn);

        const nextBtn = document.createElement('button');
        nextBtn.className = 'activity-completion-btn-next';
        nextBtn.setAttribute('aria-label', 'Next activity');
        nextBtn.innerHTML = 'Next Activity &#9654;';
        nextBtn.addEventListener('pointerup', () => {
            this._dismissCompletionModal();
            ScratchJr.saveProject(null, ScratchJr.flippage);
        });
        actions.appendChild(nextBtn);

        body.appendChild(actions);
        modal.appendChild(body);
        this._completionOverlay.appendChild(modal);
        document.body.appendChild(this._completionOverlay);
    }

    _dismissCompletionModal () {
        if (this._completionOverlay) {
            this._completionOverlay.style.opacity = '0';
            setTimeout(() => {
                if (this._completionOverlay && this._completionOverlay.parentNode) {
                    this._completionOverlay.parentNode.removeChild(this._completionOverlay);
                }
                this._completionOverlay = null;
            }, 300);
        }
    }

    _refreshScriptSignatures () {
        MEMBERS.forEach((role) => {
            this._scriptSignatures[role] = this._getSpriteSignature(role);
        });
    }

    _signatureFromEncodedScripts (strips) {
        return (Array.isArray(strips) ? strips : []).map((strip) => {
            if (!Array.isArray(strip)) {
                return '';
            }
            return strip
                .filter((entry) => Array.isArray(entry))
                .map((entry) => `${entry[0]}:${entry[1]}`)
                .join('|');
        }).join('||');
    }

    _getSpriteSignature (role) {
        return this._getSpriteBlocks(role)
            .map((block) => {
                const argValue = block && block.arg && typeof block.arg.argValue !== 'undefined'
                    ? block.arg.argValue
                    : (block && typeof block.getArgValue === 'function' ? block.getArgValue() : '');
                return `${block.blocktype}:${argValue}`;
            })
            .join('|');
    }

    _injectBluuxBug () {
        const bluux = this._getSpriteOwner('bluux');
        if (!bluux) {
            return;
        }
        const encoded = this._getEncodedScripts('bluux');
        let strip = Array.isArray(encoded[0]) ? encoded[0].slice() : [['onflag', 'null', 100, 100], ['playsnd', 'pop.mp3', 120, 100], ['endstack', 'null', 140, 100]];
        const insertion = [this._config.bugBlock.type, this._config.bugBlock.arg, 100 + (strip.length * 20), 100];
        const endIndex = strip.findIndex((entry) => Array.isArray(entry) && entry[0] === 'endstack');
        if (endIndex > -1) {
            strip.splice(endIndex, 0, insertion);
        } else {
            strip.push(insertion);
        }
        encoded[0] = strip;
        this._setEncodedScripts('bluux', encoded);
        this._bugInjected = true;
        this._injectedBugSignature = this._signatureFromEncodedScripts(encoded);
        this._showToast('oops', 'Uh-oh!');
    }

    _autoRunInjectedBug () {
        this._autoDebugPlaybackPending = true;
        this._selectRole('bluux');
        ScratchJr.stopStrips();
        ScratchJr.resetSprites();
        ScratchJr.startGreenFlagThreads();
        if (this._phaseTimeout) {
            clearTimeout(this._phaseTimeout);
        }
        this._phaseTimeout = setTimeout(() => {
            this._phaseTimeout = null;
            if (this._destroyed || this._phase !== PHASES.DEBUG_INTERLUDE || !this._autoDebugPlaybackPending) {
                return;
            }
            this._autoDebugPlaybackPending = false;
            this._queueNarration('VO-09', null, {speaker: 'system', clear: true});
            if (this._stateMachine && this._stateMachine.state !== 'BUILDING') {
                this._stateMachine.transition('BUILDING');
            }
            this._persistProgress();
        }, 1800);
    }

    _bluuxHasInjectedBug () {
        if (!this._bugInjected) {
            return false;
        }
        const encoded = this._getEncodedScripts('bluux');
        if (!Array.isArray(encoded) || encoded.length < 1) {
            return false;
        }
        const signature = this._signatureFromEncodedScripts(encoded);
        if (!this._injectedBugSignature) {
            this._injectedBugSignature = signature;
        }
        return signature === this._injectedBugSignature;
    }

    _bluuxStayedOnDrums () {
        if (this._runObservation && typeof this._runObservation.bluuxMaxDistanceFromDrums === 'number') {
            return this._runObservation.bluuxMaxDistanceFromDrums <= 18;
        }
        const bluux = this._getSpriteOwner('bluux');
        const target = this._config.members.bluux;
        if (!bluux || !target) {
            return false;
        }
        return Math.abs(bluux.xcoor - target.homeX) <= 18 && Math.abs(bluux.ycoor - target.homeY) <= 18;
    }

    _getEncodedScripts (role) {
        const sprite = this._getSpriteOwner(role);
        if (!sprite || typeof sprite.getData !== 'function') {
            return [];
        }
        const data = sprite.getData();
        return Array.isArray(data.scripts) ? JSON.parse(JSON.stringify(data.scripts)) : [];
    }

    _setEncodedScripts (role, strips) {
        const sprite = this._getSpriteOwner(role);
        const scriptsDiv = sprite ? gn(sprite.id + '_scripts') : null;
        if (!scriptsDiv || !scriptsDiv.owner) {
            return;
        }
        scriptsDiv.owner.dragList = scriptsDiv.owner.getBlocks();
        scriptsDiv.owner.deleteBlocks();
        scriptsDiv.owner.dragList = [];
        (strips || []).forEach((strip) => {
            scriptsDiv.owner.recreateStrip(strip);
        });
        this._refreshScriptSignatures();
    }

    _restoreSavedScripts (saved) {
        const characterScripts = saved && saved.character_scripts;
        if (!characterScripts || typeof characterScripts !== 'object') {
            return false;
        }

        let restored = false;
        MEMBERS.forEach((role) => {
            const savedScripts = role === 'bluux'
                ? (characterScripts.bluux || characterScripts.cat)
                : characterScripts[role];
            if (!Array.isArray(savedScripts)) {
                return;
            }
            this._setEncodedScripts(role, savedScripts);
            restored = true;
        });

        if (restored) {
            this._refreshScriptSignatures();
            this._bugInjected = this._bluuxHasInjectedBug();
        }

        return restored;
    }

    _queueNarration (id, fallbackText, opts = {}) {
        const text = fallbackText || this._config.voiceOver[id];
        if (!text) {
            return Promise.resolve();
        }

        if (opts.clear) {
            this._stopNarration();
        }

        const runId = this._speechRunId;
        this._lastNarrationId = id;
        this._lastNarrationText = text;
        const entry = {
            id,
            text,
            phase: this._phase,
            speaker: opts.speaker || 'system',
            spokenAt: new Date().toISOString()
        };
        this._voiceHistory.push(entry);
        this._voiceHistory = this._voiceHistory.slice(-30);

        this._speechQueue = this._speechQueue.then(async () => {
            if (this._destroyed || runId !== this._speechRunId) {
                return;
            }
            await this._speakUtterance(text, opts.speaker || 'system');
        });

        return this._speechQueue;
    }

    _stopNarration () {
        this._speechRunId++;
        this._speechQueue = Promise.resolve();
        if (window.speechSynthesis && typeof window.speechSynthesis.cancel === 'function') {
            try {
                window.speechSynthesis.cancel();
            } catch (_) {}
        }
    }

    _replayCurrentNarration () {
        if (this._lastNarrationId && this._lastNarrationText) {
            this._queueNarration(this._lastNarrationId, this._lastNarrationText, {
                speaker: this._speakerForPhase(this._phase),
                clear: true
            });
        }
    }

    _speakUtterance (text, speaker) {
        const preferredVoice = this._getPreferredVoice();
        const settings = this._speakerSettings(speaker);
        if (!window.speechSynthesis || typeof SpeechSynthesisUtterance === 'undefined') {
            return this._wait(Math.min(7200, Math.max(1800, text.split(/\s+/).length * 360)));
        }

        return new Promise((resolve) => {
            let resolved = false;
            let timeoutId = null;
            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                resolve();
            };

            try {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = preferredVoice && preferredVoice.lang ? preferredVoice.lang : 'en-US';
                if (preferredVoice) {
                    utterance.voice = preferredVoice;
                }
                utterance.rate = settings.rate;
                utterance.pitch = settings.pitch;
                utterance.volume = 1;
                utterance.onend = finish;
                utterance.onerror = finish;
                timeoutId = setTimeout(finish, Math.min(9000, Math.max(2200, text.split(/\s+/).length * 420)));
                window.speechSynthesis.speak(utterance);
            } catch (_) {
                finish();
            }
        });
    }

    _speakerSettings (speaker) {
        switch (speaker) {
        case 'bluux':
            return {rate: 0.94, pitch: 1.14};
        case 'dog':
            return {rate: 0.9, pitch: 0.96};
        case 'bird':
            return {rate: 1, pitch: 1.32};
        default:
            return {rate: 0.92, pitch: 1.08};
        }
    }

    _buildResumePrompt (phase) {
        switch (phase) {
        case PHASES.TASK_1_DRUMS:
            return 'Welcome back to the Bug Band! Start with Blooks. Give Blooks a green flag and a sound block so Blooks can play the drums.';
        case PHASES.CHAR_SWITCH_1:
        case PHASES.TASK_2_GUITAR:
            return 'Welcome back! Blooks is ready. Now Dog needs a program so Blooks and Dog can play together.';
        case PHASES.DEBUG_INTERLUDE:
            return 'Welcome back! Blooks still has a problem in the drum program. Find the extra block and fix it.';
        case PHASES.CHAR_SWITCH_2:
        case PHASES.TASK_3_SINGER:
            return 'Welcome back! Blooks and Dog are ready. Now help Bird join the concert!';
        case PHASES.BONUS:
            return 'Welcome back! The whole Bug Band is ready. You can add more blocks or tap Done when you are finished!';
        default:
            return this._config.voiceOver['VO-01'];
        }
    }

    _initVoiceSelection () {
        if (!window.speechSynthesis) {
            return;
        }
        this._refreshPreferredVoice();
        if (!this._speechVoice && typeof window.speechSynthesis.addEventListener === 'function') {
            window.speechSynthesis.addEventListener('voiceschanged', this._voicesChangeHandler);
        }
    }

    _waitForPreferredVoice (timeoutMs = 2600) {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return Promise.resolve();
        }
        this._refreshPreferredVoice();
        if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            let resolved = false;
            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                resolve();
            };
            const poll = setInterval(() => {
                this._refreshPreferredVoice();
                if (this._speechVoice && this._speechVoiceScore >= this._config.voiceConfidenceThreshold) {
                    clearInterval(poll);
                    clearTimeout(timeout);
                    finish();
                }
            }, 100);
            const timeout = setTimeout(() => {
                clearInterval(poll);
                finish();
            }, timeoutMs);
        });
    }

    _refreshPreferredVoice () {
        const selection = this._selectPreferredVoice();
        this._speechVoice = selection ? selection.voice : null;
        this._speechVoiceScore = selection ? selection.score : 0;
    }

    _getPreferredVoice () {
        if (!this._speechVoice) {
            this._refreshPreferredVoice();
        }
        return this._speechVoice;
    }

    _selectPreferredVoice () {
        if (!window.speechSynthesis || typeof window.speechSynthesis.getVoices !== 'function') {
            return null;
        }
        const voices = window.speechSynthesis.getVoices();
        if (!Array.isArray(voices) || voices.length < 1) {
            return null;
        }
        const ranked = voices
            .map((voice) => ({voice, score: this._scoreVoice(voice)}))
            .sort((a, b) => b.score - a.score);
        return ranked[0] && ranked[0].score > 0 ? ranked[0] : null;
    }

    _scoreVoice (voice) {
        return scorePreferredNarrationVoice(voice, window, {profile: 'bugBand'});
    }

    _wait (ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async _persistProgress (overrides = {}) {
        const now = Date.now();
        const elapsedMs = Math.max(0, now - this._sessionStartedAt - this._idleTimeMs);
        const completedPhase = this._phase === PHASES.COMPLETE || this._phase === PHASES.FULL_CONCERT || this._phase === PHASES.BONUS;
        const status = overrides.status || (completedPhase ? 'completed' : 'in_progress');
        const completedAt = Object.prototype.hasOwnProperty.call(overrides, 'completedAt')
            ? overrides.completedAt
            : (status === 'completed' ? new Date().toISOString() : undefined);
        await ActivityStorageService.saveProgress(this._activityData.activityId, {
            status,
            attempts: overrides.attempts !== undefined ? overrides.attempts : this._flagTapCount,
            stars: overrides.stars !== undefined ? overrides.stars : (status === 'completed' ? 3 : 0),
            bestScore: overrides.bestScore !== undefined ? overrides.bestScore : (status === 'completed' ? 3 : 0),
            hintsUsed: this._hintEvents.length,
            currentState: this._phase,
            totalActiveTimeSec: Math.round(elapsedMs / 1000),
            hintEvents: this._hintEvents.slice(),
            outcomeFlags: {...this._outcomeFlags},
            flagTapCount: this._flagTapCount,
            characterSwitches: this._characterSwitches,
            debugAttempts: this._debugAttempts,
            debugSuccess: this._debugSuccess,
            bugInjected: this._bugInjected,
            injectedBugSignature: this._bugInjected ? this._injectedBugSignature : null,
            repeatAnnounced: this._repeatAnnounced,
            milestones: {...this._milestones},
            character_scripts: {
                bluux: this._getEncodedScripts('bluux'),
                dog: this._getEncodedScripts('dog'),
                bird: this._getEncodedScripts('bird')
            },
            completedAt: completedAt,
            lastOpenedAt: new Date().toISOString()
        });
    }

    _resetForReplay () {
        this._clearIdleTimers();
        this._clearHintVisuals();
        this._hideDoneButton();
        this._dismissCompletionModal();
        this._closeAllBubbles();
        this._stopNarration();
        this._stopRunMonitor();
        this._bugInjected = false;
        this._injectedBugSignature = null;
        this._repeatAnnounced = false;
        this._debugSuccess = false;
        this._autoDebugPlaybackPending = false;
        this._runObservation = null;
        this._flagTapCount = 0;
        this._characterSwitches = 0;
        this._debugAttempts = 0;
        this._outcomeFlags = {
            outcome_1: false,
            outcome_2: false,
            outcome_3: false,
            outcome_4: false,
            outcome_5: false,
            outcome_6: false
        };
        this._milestones = {
            bluuxFlag: false,
            bluuxReady: false,
            dogFlag: false,
            dogReady: false,
            birdFlag: false,
            birdReady: false
        };
        this._hintEvents = [];
        this._voiceHistory = [];
        this._sessionStartedAt = Date.now();
        this._lastInteractionAt = Date.now();
        this._idlePausedAt = null;
        this._idleTimeMs = 0;
        ScratchJr.stopStrips();
        ScratchJr.resetSprites();
        MEMBERS.forEach((role) => this._setEncodedScripts(role, []));
        this._setPhase(this._config.restartPhase || PHASES.TASK_1_DRUMS, {silent: true});
        this._selectRole('bluux');
        this._refreshScriptSignatures();
        this._syncVisualState();
        this._startIdleScaffolding();
        this._queueNarration('VO-02', null, {speaker: 'bluux', clear: true});
        this._persistProgress({
            status: 'in_progress',
            completedAt: null
        });
    }
}
